import { useState } from 'react';
import Swal from 'sweetalert2';
import type { ListenerProfile as ListenerProfileType } from '@/mocks/listenerCenter';

interface Props {
  profile: ListenerProfileType;
}

const MOCK_AVATARS = [
  'https://readdy.ai/api/search-image?query=Young%20Hispanic%20male%20portrait%20with%20friendly%20warm%20smile%2C%20casual%20modern%20style%2C%20wearing%20simple%20navy%20t-shirt%2C%20clean%20soft%20background%20with%20warm%20natural%20lighting%2C%20approachable%20authentic%20headshot%20photography%2C%20editorial%20portrait%20style&width=400&height=400&seq=listener-avatar-1&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Friendly%20young%20man%20portrait%20with%20genuine%20smile%2C%20casual%20denim%20jacket%2C%20urban%20street%20style%2C%20soft%20natural%20window%20lighting%2C%20clean%20minimal%20background%2C%20authentic%20editorial%20portrait%20photography&width=400&height=400&seq=listener-avatar-2&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Young%20man%20portrait%20with%20gentle%20smile%2C%20wearing%20black%20hoodie%2C%20modern%20casual%20look%2C%20soft%20studio%20lighting%20on%20gradient%20warm%20beige%20background%2C%20contemporary%20headshot%20photography&width=400&height=400&seq=listener-avatar-3&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Hispanic%20young%20adult%20male%20portrait%20with%20confident%20expression%2C%20wearing%20olive%20green%20button%20shirt%2C%20warm%20studio%20lighting%2C%20clean%20cream%20background%2C%20professional%20headshot%20photography%20approachable&width=400&height=400&seq=listener-avatar-4&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Young%20man%20smiling%20portrait%2C%20casual%20white%20tshirt%2C%20natural%20sunlight%20through%20window%20creating%20soft%20shadows%2C%20warm%20ambient%20atmosphere%2C%20relaxed%20authentic%20lifestyle%20photography&width=400&height=400&seq=listener-avatar-5&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Portrait%20of%20young%20man%20with%20warm%20kind%20expression%2C%20wearing%20casual%20light%20blue%20polo%20shirt%2C%20clean%20studio%20background%20with%20soft%20gradient%20lighting%2C%20professional%20yet%20friendly%20headshot&width=400&height=400&seq=listener-avatar-6&orientation=squarish',
];

export default function ListenerProfile({ profile }: Props) {
  const [avatarUrl, setAvatarUrl] = useState(profile.avatarUrl);
  const [editOpen, setEditOpen] = useState(false);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [formData, setFormData] = useState({
    name: profile.name,
    bio: profile.bio,
    location: profile.location,
    email: profile.email,
  });

  const handleSaveProfile = () => {
    setEditOpen(false);
    Swal.fire({
      title: 'Perfil actualizado',
      text: 'Tus datos se guardaron correctamente.',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#10b981',
      timer: 2500,
      timerProgressBar: true,
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    });
  };

  const handleAvatarSelect = (newAvatar: string) => {
    setAvatarUrl(newAvatar);
    setAvatarPickerOpen(false);
    Swal.fire({
      title: 'Avatar actualizado',
      text: 'Tu foto de perfil se cambió correctamente.',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#10b981',
      timer: 2500,
      timerProgressBar: true,
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    });
  };

  const handleShareProfile = async () => {
    const shareUrl = `${window.location.origin}/listener`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Perfil de ${profile.name}`,
          text: `Mirá el perfil de ${profile.name} en RadioFrecuenciaWeb`,
          url: shareUrl,
        });
      } catch {
        // user cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        Swal.fire({
          toast: true,
          position: 'top-end',
          title: 'Link copiado al portapapeles',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
          customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
          buttonsStyling: false,
        });
      } catch {
        Swal.fire({
          title: 'No se pudo compartir',
          text: 'Intentá copiar la URL manualmente.',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#f59e0b',
          customClass: {
            popup: 'rounded-2xl',
            title: 'font-bold',
            confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
            htmlContainer: 'text-sm',
          },
          buttonsStyling: false,
        });
      }
    }
  };

  const handleToggleFollow = () => {
    setIsFollowing(!isFollowing);
    Swal.fire({
      toast: true,
      position: 'top-end',
      title: isFollowing ? 'Dejaste de seguir este perfil' : 'Ahora seguís este perfil',
      icon: isFollowing ? 'info' : 'success',
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
      buttonsStyling: false,
    });
  };

  const badgeColors: Record<string, string> = {
    'Oyente Fiel': 'bg-amber-100 text-amber-800',
    'Madrugador': 'bg-sky-100 text-sky-800',
    'Mega Fan': 'bg-rose-100 text-rose-800',
    'Comunicador': 'bg-emerald-100 text-emerald-800',
    'Oyente Legendario': 'bg-purple-100 text-purple-800',
  };

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-background-50 rounded-2xl p-6 md:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="relative shrink-0 group">
            <img
              src={avatarUrl}
              alt={profile.name}
              className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover object-top cursor-pointer"
              onClick={() => setAvatarPickerOpen(true)}
            />
            {/* Avatar change overlay */}
            <div
              className="absolute inset-0 rounded-2xl bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-all flex items-center justify-center cursor-pointer"
              onClick={() => setAvatarPickerOpen(true)}
            >
              <div className="w-8 h-8 rounded-full bg-background-50/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                <i className="ri-camera-line text-foreground-700 text-sm"></i>
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-accent-500 rounded-full flex items-center justify-center border-2 border-background-50">
              <i className="ri-check-line text-background-50 text-xs"></i>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">{profile.name}</h2>
              <span className="text-sm text-foreground-500 font-medium">{profile.username}</span>
            </div>
            <p className="text-sm text-foreground-600 mb-3">{profile.bio}</p>
            <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-map-pin-line"></i>
                {profile.location}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-mail-line"></i>
                {profile.email}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                Miembro desde {new Date(profile.joinDate).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleToggleFollow}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                isFollowing
                  ? 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200'
                  : 'bg-secondary-500 hover:bg-secondary-600 text-background-50'
              }`}
            >
              <i className={`${isFollowing ? 'ri-user-unfollow-line' : 'ri-user-follow-line'} mr-1.5`}></i>
              {isFollowing ? 'Siguiendo' : 'Seguir'}
            </button>
            <button
              onClick={handleShareProfile}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-all cursor-pointer"
              title="Compartir perfil"
            >
              <i className="ri-share-line"></i>
            </button>
            <button
              onClick={() => setEditOpen(true)}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line mr-1.5"></i>
              Editar Perfil
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {[
          { label: 'Horas Escuchadas', value: profile.stats.totalListeningHours.toLocaleString(), icon: 'ri-headphone-line', color: 'bg-primary-100 text-primary-700' },
          { label: 'Mensajes Enviados', value: profile.stats.messagesSent.toString(), icon: 'ri-message-2-line', color: 'bg-accent-100 text-accent-700' },
          { label: 'Favoritas', value: profile.stats.radiosFavorited.toString(), icon: 'ri-heart-line', color: 'bg-rose-100 text-rose-700' },
          { label: 'Siguiendo', value: profile.stats.radiosFollowed.toString(), icon: 'ri-user-follow-line', color: 'bg-sky-100 text-sky-700' },
          { label: 'Eventos', value: profile.stats.eventsAttended.toString(), icon: 'ri-calendar-check-line', color: 'bg-emerald-100 text-emerald-700' },
          { label: 'Insignias', value: profile.stats.badges.length.toString(), icon: 'ri-award-line', color: 'bg-amber-100 text-amber-700' },
        ].map((stat) => (
          <div key={stat.label} className="bg-background-50 rounded-xl p-4 text-center">
            <div className={`w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center ${stat.color}`}>
              <i className={`${stat.icon} text-lg`}></i>
            </div>
            <div className="text-xl md:text-2xl font-bold text-foreground-950">{stat.value}</div>
            <div className="text-xs text-foreground-500 mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Badges */}
      <div className="bg-background-50 rounded-2xl p-6">
        <h3 className="text-sm font-semibold text-foreground-950 mb-4">Insignias</h3>
        <div className="flex flex-wrap gap-2">
          {profile.stats.badges.map((badge) => (
            <span
              key={badge}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${badgeColors[badge] || 'bg-foreground-100 text-foreground-700'}`}
            >
              <i className="ri-verified-badge-line text-sm"></i>
              {badge}
            </span>
          ))}
        </div>
      </div>

      {/* Edit Profile Modal */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={() => setEditOpen(false)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground-950">Editar Perfil</h3>
              <button onClick={() => setEditOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={3}
                  maxLength={200}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                />
                <div className="text-xs text-foreground-400 mt-1">{formData.bio.length}/200</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Ubicación</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditOpen(false)}
                className="flex-1 px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveProfile}
                className="flex-1 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Avatar Picker Modal */}
      {avatarPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={() => setAvatarPickerOpen(false)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground-950">Cambiar Avatar</h3>
              <button onClick={() => setAvatarPickerOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-xs text-foreground-500 mb-4">Seleccioná una imagen para tu perfil.</p>
            <div className="grid grid-cols-3 gap-3">
              {MOCK_AVATARS.map((url, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAvatarSelect(url)}
                  className={`relative rounded-xl overflow-hidden cursor-pointer transition-all border-2 ${
                    avatarUrl === url ? 'border-primary-500 ring-2 ring-primary-200' : 'border-transparent hover:border-foreground-200'
                  }`}
                >
                  <img
                    src={url}
                    alt={`Avatar opción ${idx + 1}`}
                    className="w-full aspect-square object-cover object-top"
                  />
                  {avatarUrl === url && (
                    <div className="absolute top-1.5 right-1.5 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-background-50 text-xs"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => setAvatarPickerOpen(false)}
              className="w-full mt-4 px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}